import { useCallback, useLayoutEffect, useRef, useState, type Ref } from "react";
import { Box, IconButton, Skeleton, Tooltip, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import { observer } from "mobx-react-lite";
import { useQueryClient } from "@tanstack/react-query";
import { useAxios } from "@/contexts/AxiosContext";
import { useImageEntity } from "@/api/api";
import { useThumbnailImageSrc } from "@/hooks/useThumbnailImageSrc";
import { hasLibraryVideoId, setLibraryImageDragData } from "@/utils/dragTransfer";
import type { FavoriteMediaKind } from "@/services/project-entity";
import type { LibraryItem } from "./libraryItems";
import { prefetchLibraryMediaAssets } from "./libraryMediaPrefetch";

const LIBRARY_HIGHLIGHT_BORDER_COLOR = "#66BB6A";
const LIBRARY_CARD_ICON_SIZE = 24;
const LIBRARY_CARD_ICON_INSET = 4;

export interface LibraryImageCardProps {
  readonly projectId: string | null;
  readonly item: LibraryItem;
  readonly onSelect: (imageId: string, videoId?: string) => void;
  /** False on imageFrame/video panels when this row's media type cannot be placed via drag or double-click. */
  readonly dragEnabled?: boolean;
  /** True when this image is the one currently shown in mainspace (active shot's images[0]). */
  readonly isSelected?: boolean;
  /** True while this card is being dragged (visual feedback). */
  readonly isDragging?: boolean;
  /** Transient green glow when opened from a completed generation notification. */
  readonly isHighlighted?: boolean;
  readonly highlightRef?: Ref<HTMLDivElement>;
  readonly onDragStart?: (imageId: string, libraryVideoId?: string | null) => void;
  readonly onDragEnd?: () => void;
  /** When set, show play button; on click we fetch presigned URL via video entity API and play. */
  readonly videoId?: string | null;
  readonly onPlayVideo?: (videoId: string) => void;
  readonly onToggleFavorite?: (mediaId: string, kind: FavoriteMediaKind) => void;
}

const LibraryImageCard = observer(
  ({
    projectId,
    item,
    onSelect,
    dragEnabled = true,
    isSelected,
    isDragging,
    isHighlighted = false,
    highlightRef,
    onDragStart,
    onDragEnd,
    videoId,
    onPlayVideo,
    onToggleFavorite
  }: LibraryImageCardProps) => {
    const theme = useTheme();
    const axiosClient = useAxios();
    const queryClient = useQueryClient();
    const favoriteMediaId = item.libraryVideoId ?? item.imageId;
    const favoriteMediaKind: FavoriteMediaKind = item.libraryVideoId ? "video" : "image";
    const isFavorite = item.isFavorite;
    const { data, isLoading } = useImageEntity(projectId, item.imageId);
    const { src: thumbnailSrc, onError: onThumbnailError } = useThumbnailImageSrc(data);
    const titleRef = useRef<HTMLSpanElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    const title = item.displayName ?? "";

    useLayoutEffect(() => {
      const el = titleRef.current;

      if (!el) {
        return;
      }

      const checkTruncated = (): void => {
        setIsTruncated(el.scrollWidth > el.clientWidth);
      };

      checkTruncated();
      const ro = new ResizeObserver(checkTruncated);
      ro.observe(el);

      return () => ro.disconnect();
    }, [title]);

    const dragVideoId = hasLibraryVideoId(videoId) ? (videoId as string).trim() : undefined;

    const handleDragStart = useCallback(
      (e: React.DragEvent) => {
        setLibraryImageDragData(e.dataTransfer, item.imageId, dragVideoId);
        e.dataTransfer.effectAllowed = "copy";
        onDragStart?.(item.imageId, dragVideoId ?? null);

        if (projectId) {
          void prefetchLibraryMediaAssets(queryClient, axiosClient, projectId, item.imageId, dragVideoId);
        }
      },
      [axiosClient, dragVideoId, item.imageId, onDragStart, projectId, queryClient]
    );

    const handleDoubleClick = useCallback(async () => {
      if (projectId) {
        await prefetchLibraryMediaAssets(queryClient, axiosClient, projectId, item.imageId, dragVideoId);
      }

      onSelect(item.imageId, dragVideoId);
    }, [axiosClient, dragVideoId, item.imageId, onSelect, projectId, queryClient]);

    const handleDragEnd = useCallback(() => {
      onDragEnd?.();
    }, [onDragEnd]);

    const handleToggleFavorite = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onToggleFavorite?.(favoriteMediaId, favoriteMediaKind);
      },
      [favoriteMediaId, favoriteMediaKind, onToggleFavorite]
    );

    if (isLoading) {
      return <Skeleton variant="rounded" animation="wave" sx={{ borderRadius: 1, aspectRatio: "16/9", width: "100%", minHeight: "92px" }} />;
    }

    const hasImage = Boolean(projectId) && item.imageId.trim() !== "";
    const canDrag = hasImage && dragEnabled;
    let dragCursor: "default" | "grab" | "grabbing" = "default";
    if (canDrag) {
      dragCursor = isDragging ? "grabbing" : "grab";
    }

    return (
      <Box
        ref={highlightRef}
        data-testid="library-image-card"
        data-library-item-id={item.id}
        draggable={canDrag}
        onDoubleClick={canDrag ? () => void handleDoubleClick() : undefined}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        sx={{
          position: "relative",
          aspectRatio: "16/9",
          borderRadius: 1,
          overflow: "hidden",
          bgcolor: "action.hover",
          cursor: dragCursor,
          outline: "3px solid transparent",
          outlineOffset: "-3px",
          ...(isSelected ? { outlineColor: theme.palette.common.white } : {}),
          ...(isHighlighted
            ? {
                outlineColor: LIBRARY_HIGHLIGHT_BORDER_COLOR,
                zIndex: 1
              }
            : {}),
          ...(isDragging ? { opacity: 0.6 } : {}),
          "&:hover": {
            opacity: 0.95,
            ...(canDrag ? { cursor: dragCursor } : {})
          },
          "&:active": canDrag ? { cursor: "grabbing" } : {},
          "&:hover .library-card-fav-button": { opacity: 1 },
          "&:hover .library-card-title-overlay": { opacity: 1 },
          "& .library-card-title-overlay": {
            transition: "opacity 0.2s ease"
          }
        }}
      >
        {hasImage ? (
          <IconButton
            className="library-card-fav-button"
            size="small"
            aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
            onClick={handleToggleFavorite}
            sx={{
              position: "absolute",
              top: LIBRARY_CARD_ICON_INSET,
              right: LIBRARY_CARD_ICON_INSET,
              zIndex: 1,
              opacity: isFavorite ? 1 : 0,
              transition: "opacity 0.2s ease, background-color 0.2s ease",
              width: LIBRARY_CARD_ICON_SIZE,
              height: LIBRARY_CARD_ICON_SIZE,
              borderRadius: "50%",
              bgcolor: alpha(theme.palette.common.black, 0.85),
              color: theme.palette.common.white,
              "&:hover": {
                bgcolor: theme.palette.grey[300]
              }
            }}
          >
            {isFavorite ? <FavoriteIcon sx={{ fontSize: 16 }} /> : <FavoriteBorderOutlinedIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        ) : null}
        {thumbnailSrc ? (
          <Box
            component="img"
            src={thumbnailSrc}
            crossOrigin="anonymous"
            onError={onThumbnailError}
            loading="lazy"
            decoding="async"
            alt={title}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block"
            }}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              —
            </Typography>
          </Box>
        )}
        {item.isApproved ? (
          <Box
            sx={{
              position: "absolute",
              top: 4,
              left: 4,
              zIndex: 1,
              height: 20,
              display: "flex",
              alignItems: "center",
              px: 0.75,
              borderRadius: "4px",
              bgcolor: alpha(theme.palette.common.black, 0.85)
            }}
          >
            <Typography sx={{ fontSize: 10, fontWeight: 500, lineHeight: 1, color: theme.palette.common.white }}>Approved</Typography>
          </Box>
        ) : null}
        {videoId && onPlayVideo ? (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onPlayVideo(videoId);
            }}
            aria-label="Play video"
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 1,
              bgcolor: alpha(theme.palette.common.black, 0.4),
              color: theme.palette.common.white,
              "&:hover": {
                bgcolor: alpha(theme.palette.common.black, 0.6)
              }
            }}
          >
            <PlayArrowIcon sx={{ fontSize: 16 }} />
          </IconButton>
        ) : null}
        <Tooltip title={isTruncated ? title : ""} arrow placement="bottom" disableHoverListener={!isTruncated}>
          <Typography
            ref={titleRef}
            variant="caption"
            className="library-card-title-overlay"
            component="span"
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: 26,
              display: "flex",
              alignItems: "center",
              px: 1,
              bgcolor: alpha(theme.palette.common.black, 0.85),
              color: theme.palette.common.white,
              fontSize: 12,
              fontWeight: 500,
              opacity: 0,
              textAlign: "left",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
          >
            {title}
          </Typography>
        </Tooltip>
      </Box>
    );
  }
);

export default LibraryImageCard;
